import {
  Button,
  ButtonProps,
  Col,
  Collapse,
  useMaxWindowSize,
  useRawWindowSize,
} from "@modelve/ui";
import clsx from "clsx";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

interface PortalPanelChildProps extends Pick<
  PortalPanelContextState,
  "panelState" | "toggle"
> {}

interface PanelComponentProps {
  type: string;
  children?:
    | React.ReactNode
    | ((props: PortalPanelChildProps) => React.ReactNode);
}

export interface PortalPanelContextState {
  mode: "docked" | "floating";
  locked: boolean;
  setLocked: React.Dispatch<React.SetStateAction<boolean>>;
  panelState: string | false;
  toggle: (dispatch?: React.SetStateAction<string | false> | undefined) => void;
  panelTarget: HTMLElement | null;
  setPanelTarget: (portalTarget: HTMLElement | null) => void;
  actionsTarget: HTMLElement | null;
  setActionsTarget: (portalTarget: HTMLElement | null) => void;
}

export const PortalPanelContext = createContext<PortalPanelContextState>({
  mode: "docked",
  locked: false,
  actionsTarget: null,
  panelTarget: null,
  panelState: false,
  setLocked: () => {},
  setActionsTarget: () => {},
  setPanelTarget: () => {},
  toggle: () => false,
});

export function usePortalPanel() {
  return useContext(PortalPanelContext);
}

function PortalPanelActions(props: Omit<PanelComponentProps, "type">) {
  const { actionsTarget, panelState, toggle } = usePortalPanel();

  if (actionsTarget == null) {
    return null;
  }

  let children: React.ReactNode;

  if (typeof props.children === "function") {
    children = props.children({
      panelState: panelState,
      toggle: toggle,
    });
  } else {
    children = props.children;
  }

  return createPortal(children, actionsTarget);
}

interface PortalPanelActionToggleProps extends ButtonProps {
  label?: React.ReactNode;
}

function PortalPanelActionToggle(
  props: Readonly<PortalPanelActionToggleProps>,
) {
  const { className, children, label, disabled, ...rest } = props;
  const { locked, panelState, mode } = usePortalPanel();

  const [hover, setHover] = useState(false);

  return (
    <Button
      {...rest}
      className={twMerge(
        clsx("w-fit h-12 shadow-xl flex items-center", {
          "cursor-not-allowed animate-none!": disabled,
          "rounded-s-full": mode === "docked" || !panelState,
          "rounded-full": mode === "floating" && panelState,
        }),
        className,
      )}
      disabled={disabled || locked}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
      {label && (
        <Collapse in={hover} dimension="width">
          <div className="ps-2 whitespace-nowrap">{label}</div>
        </Collapse>
      )}
    </Button>
  );
}

function PortalPanelDrawer(props: PanelComponentProps) {
  const { type } = props;
  const { panelTarget, panelState, toggle } = usePortalPanel();

  if (panelTarget == null || type !== panelState) {
    return null;
  }

  let children: React.ReactNode;

  if (typeof props.children === "function") {
    children = props.children({
      panelState: panelState,
      toggle: toggle,
    });
  } else {
    children = props.children;
  }

  return createPortal(children, panelTarget);
}

function PortalPanelProvider(props: React.PropsWithChildren) {
  const { children } = props;

  const [locked, setLocked] = useState(false);
  const [panelState, setPanelState] = useState<string | false>(false);
  const [panelTarget, setPanelTarget] = useState<HTMLElement | null>(null);
  const [panelActionsTarget, setPanelActionsTarget] =
    useState<HTMLElement | null>(null);

  const shouldFloat = useMaxWindowSize("xl");
  const { width } = useRawWindowSize();

  const toggle = useCallback(
    (dispatch?: React.SetStateAction<string | false>) =>
      setPanelState((state) => {
        if (typeof dispatch === "function") {
          return dispatch(state);
        }
        return dispatch && dispatch !== state ? dispatch : false;
      }),
    [],
  );

  const context = useMemo<PortalPanelContextState>(
    () => ({
      mode: shouldFloat ? "floating" : "docked",
      locked: locked,
      panelState: panelState,
      actionsTarget: panelActionsTarget,
      panelTarget: panelTarget,
      setLocked: setLocked,
      setActionsTarget: setPanelActionsTarget,
      setPanelTarget: setPanelTarget,
      toggle: toggle,
    }),
    [shouldFloat, locked, panelState, panelTarget, panelActionsTarget, toggle],
  );

  return (
    <PortalPanelContext.Provider value={context}>
      {children}
      <button
        type="button"
        aria-label="Close panel"
        className={clsx(
          "fixed inset-0 z-10 bg-black/50 duration-300 transition-opacity",
          {
            "opacity-100": shouldFloat && panelState,
            "opacity-0 pointer-events-none": !shouldFloat || !panelState,
          },
        )}
        onClick={() => setPanelState(false)}
      />
      <div
        className={clsx(
          "shrink-0 bg-white drop-shadow-xl border-l border-l-hunter-300/50 transition-[width] duration-300 overflow-x-hidden overflow-y-auto z-10",
          {
            "h-screen": !shouldFloat,
            "absolute top-0 right-0 bottom-0": shouldFloat,
          },
        )}
        style={{
          width: panelState ? Math.min(512, width) : 0,
        }}
      >
        <div
          ref={setPanelTarget}
          style={{
            width: Math.min(512, width),
            paddingBottom: shouldFloat
              ? (panelActionsTarget?.clientHeight ?? 0) + 32
              : 0,
          }}
        />
      </div>
      <Col
        ref={setPanelActionsTarget}
        className={clsx(
          "absolute bottom-8 transition-all duration-300 items-end",
          {
            "z-9 *:z-9": !shouldFloat,
            "z-11 *:z-11": shouldFloat,
            "right-0": !panelState,
            "right-4": panelState && shouldFloat,
            "right-128": panelState && !shouldFloat,
          },
        )}
        gap="md"
      />
    </PortalPanelContext.Provider>
  );
}

const PortalPanel = Object.assign(PortalPanelProvider, {
  Panel: PortalPanelDrawer,
  Actions: PortalPanelActions,
  Toggle: PortalPanelActionToggle,
});

export default PortalPanel;
